// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Button,
  Card,
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createReference, formatHumanName, normalizeErrorString } from '@medplum/core';
import type {
  BundleEntry,
  DocumentReference,
  HumanName,
  MedicationRequest,
  Observation,
  Patient,
  Practitioner,
  Provenance,
  Reference,
  Task,
} from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile, useResource } from '@medplum/react';
import { CLOUDPAT_OBSERVATION_SYSTEM, getCurrentRpmMonth, RPM_OBSERVATION_CODES } from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { IconCheck, IconDownload, IconEdit, IconFilePlus, IconLock } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { PageContainer } from '../../components/layout/PageContainer';
import { TASK_ASSOCIATED_ACTION_URL } from '../../constants/task-associated-action';
import {
  attachPrescriptionDocumentToTask,
  createPrescriptionDocument,
  prescriptionPdfFilename,
  renderPrescriptionPdfBlob,
} from '../../utils/prescription-pdf';
import { RPM_ALERT_THRESHOLDS, RPM_MONITORING_PARAMETERS, RPM_ORDER_SYSTEM } from '../../utils/rpm-order-pdf';
import {
  buildRpmOrderSignatureProvenance,
  getOrderSignerForSave,
  getStoredOrderSigner,
} from '../../utils/rpm-order-signature';
import { buildRpmEnrollmentActivationEntry, notifyRpmEnrollmentUpdated } from '../../utils/rpmEnrollment';
import { upsertRpmObservation } from '../../utils/rpmFhir';
import { withTenantScope } from '../../utils/tenant';
import classes from './RpmOrderPage.module.css';

const RPM_TASK_IDENTIFIER_SYSTEM = 'https://polarissleep.com/fhir/task/rpm-order';
const DIAGNOSIS_OPTIONS = [
  { value: 'Obstructive sleep apnea (G47.33)', label: 'Obstructive sleep apnea (G47.33)' },
  { value: 'Sleep apnea, unspecified (G47.30)', label: 'Sleep apnea, unspecified (G47.30)' },
  { value: 'Central sleep apnea (G47.31)', label: 'Central sleep apnea (G47.31)' },
  { value: 'Other', label: 'Other / specify' },
];
const SEVERITY_OPTIONS = [
  { value: 'Mild', label: 'Mild', sub: 'AHI 5–15' },
  { value: 'Moderate', label: 'Moderate', sub: 'AHI 16–30' },
  { value: 'Severe', label: 'Severe', sub: 'AHI >30' },
];
const DURATION_OPTIONS = ['Initial 90 days', 'Ongoing', 'Other'];

function patientName(patient: Patient): string {
  return patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Patient';
}

function providerName(provider: Practitioner | undefined): string {
  return provider?.name?.[0] ? formatHumanName(provider.name[0]) : 'Current provider';
}

function formatDisplayDate(value: string): string {
  const [year, month, day] = value.split('-');
  return year && month && day ? `${month}/${day}/${year}` : value;
}

function openInNewWindow(path: string): void {
  window.open(path, '_blank', 'noopener,noreferrer');
}

function getAhi(observation: Observation | undefined): number | undefined {
  return observation?.valueQuantity?.value ?? observation?.valueInteger;
}

function getSignatureLabel(
  signed: boolean,
  readOnly: boolean,
  order: MedicationRequest | undefined,
  currentProvider: Practitioner | undefined
): string {
  if (!signed) {
    return 'Click to sign electronically';
  }
  if (readOnly) {
    return order?.requester?.display ?? 'Signer not documented';
  }
  return providerName(currentProvider);
}

export function RpmOrderPage(): JSX.Element {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const patientReference = useMemo<Reference<Patient> | undefined>(
    () => (patientId ? { reference: `Patient/${patientId}` } : undefined),
    [patientId]
  );
  const patient = useResource<Patient>(patientReference);
  const encounterId = searchParams.get('encounterId') ?? undefined;
  const prescriptionId = searchParams.get('prescriptionId') ?? undefined;
  const prescriptionReference = useMemo<Reference<MedicationRequest> | undefined>(
    () => (prescriptionId ? { reference: `MedicationRequest/${prescriptionId}` } : undefined),
    [prescriptionId]
  );
  const prescription = useResource<MedicationRequest>(prescriptionReference);
  const storedSigner = useResource<Practitioner>(getStoredOrderSigner(prescription));
  const [editing, setEditing] = useState(false);
  const readOnly = Boolean(prescriptionId) && !editing;
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [diagnosis, setDiagnosis] = useState(DIAGNOSIS_OPTIONS[0].value);
  const [secondaryDiagnosis, setSecondaryDiagnosis] = useState('');
  const [otherDiagnosis, setOtherDiagnosis] = useState('');
  const [ahi, setAhi] = useState<number | undefined>();
  const [studyDate, setStudyDate] = useState('');
  const automateTask = true;
  const [severity, setSeverity] = useState('');
  const [duration, setDuration] = useState(DURATION_OPTIONS[0]);
  const [parameters, setParameters] = useState<Record<string, boolean>>({
    usage: true,
    rahi: true,
    leak: true,
    pressure: true,
    spo2: false,
  });
  const [alerts, setAlerts] = useState<Record<string, boolean>>({
    usage: true,
    rahi: true,
    leak: true,
    spo2: false,
    symptoms: false,
  });
  const [deviceManufacturer, setDeviceManufacturer] = useState('ResMed');
  const [deviceModel, setDeviceModel] = useState('AirSense 11');
  const [deviceMode, setDeviceMode] = useState('APAP');
  const [devicePressure, setDevicePressure] = useState('');
  const [signed, setSigned] = useState(false);
  const [storedDocument, setStoredDocument] = useState<DocumentReference>();
  const [storedDocumentUrl, setStoredDocumentUrl] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [orderDate, setOrderDate] = useState(today);
  const displayedProvider = readOnly ? storedSigner : profile;
  const providerNpi =
    displayedProvider?.identifier?.find((identifier) => identifier.system?.toLowerCase().includes('npi'))?.value ?? '';
  const signatureLabel = getSignatureLabel(signed, readOnly, prescription, profile);
  // Only a saved, signed order can be exported: the PDF documents the signature on record, not the unsaved form.
  const canExport = readOnly && signed && Boolean(prescription?.id && storedSigner);

  useEffect(() => {
    if (!prescription) {
      return;
    }
    if (prescription.authoredOn) {
      setOrderDate(prescription.authoredOn.slice(0, 10));
    }
    const noteText =
      prescription.note
        ?.map((note) => note.text)
        .filter(Boolean)
        .join('\n') ?? '';
    const diagnosisFromNote = noteText.match(/^Diagnosis: (.+)$/m)?.[1];
    const ahiFromNote = noteText.match(/^AHI from study results: (.+)$/m)?.[1];
    if (diagnosisFromNote) {
      if (DIAGNOSIS_OPTIONS.some((option) => option.value === diagnosisFromNote)) {
        setDiagnosis(diagnosisFromNote);
      } else {
        setDiagnosis('Other');
        setOtherDiagnosis(diagnosisFromNote);
      }
    }
    if (ahiFromNote && ahiFromNote !== 'Not available') {
      const parsedAhi = Number.parseFloat(ahiFromNote);
      if (!Number.isNaN(parsedAhi)) {
        setAhi(parsedAhi);
      }
    }
    const severityFromNote = noteText.match(/^OSA Severity: (.+)$/m)?.[1];
    const secondaryDiagnosisFromNote = noteText.match(/^Secondary Diagnosis: (.+)$/m)?.[1];
    const studyDateFromNote = noteText.match(/^Date of Sleep Study: (.+)$/m)?.[1];
    const durationFromNote = noteText.match(/^Monitoring Duration: (.+)$/m)?.[1];
    if (severityFromNote) {
      setSeverity(severityFromNote);
    }
    if (secondaryDiagnosisFromNote && secondaryDiagnosisFromNote !== 'None') {
      setSecondaryDiagnosis(secondaryDiagnosisFromNote);
    }
    if (studyDateFromNote && /^\d{4}-\d{2}-\d{2}$/.test(studyDateFromNote)) {
      setStudyDate(studyDateFromNote);
    }
    if (durationFromNote) {
      setDuration(durationFromNote);
    }
    setSigned(Boolean(prescription.requester?.reference));
  }, [prescription]);

  useEffect(() => {
    if (!prescription?.id) {
      return;
    }
    medplum
      .searchOne('DocumentReference', { related: `MedicationRequest/${prescription.id}` })
      .then((document) => setStoredDocument(document as DocumentReference | undefined))
      .catch(() => setStoredDocument(undefined));
  }, [medplum, prescription?.id]);

  useEffect(() => {
    return () => {
      if (storedDocumentUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(storedDocumentUrl);
      }
    };
  }, [storedDocumentUrl]);

  const handleViewStoredDocument = async (): Promise<void> => {
    const rawUrl = storedDocument?.content?.[0]?.attachment?.url;
    if (!rawUrl) {
      return;
    }
    const url = rawUrl.startsWith('Binary/') ? URL.createObjectURL(await medplum.download(rawUrl)) : rawUrl;
    setStoredDocumentUrl(url);
  };

  useEffect(() => {
    if (!patient?.id) {
      return;
    }
    medplum
      .searchResources('Observation', {
        subject: `Patient/${patient.id}`,
        code: `${CLOUDPAT_OBSERVATION_SYSTEM}|AHI`,
        _sort: '-_lastUpdated',
        _count: '1',
      })
      .then((results) => setAhi(getAhi(results[0] as Observation | undefined)))
      .catch(() => setAhi(undefined));
  }, [medplum, patient?.id]);

  const handleExport = async (): Promise<void> => {
    if (!canExport || !prescription?.id || !storedSigner) {
      return;
    }
    setExporting(true);
    try {
      await Sentry.startSpan({ op: 'ui.click', name: 'Export RPM Order PDF' }, async () => {
        const signature = (await medplum.searchOne('Provenance', {
          target: `MedicationRequest/${prescription.id}`,
          _sort: '-recorded',
        })) as Provenance | undefined;
        const signedAt = signature?.recorded ?? prescription.authoredOn ?? new Date().toISOString();
        const blob = await renderPrescriptionPdfBlob(medplum, patient as Patient, storedSigner, prescription, signedAt);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = prescriptionPdfFilename(patient as Patient, prescription);
        link.click();
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      Sentry.captureException(error);
      notifications.show({ color: 'red', message: `Could not export RPM order: ${normalizeErrorString(error)}` });
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!patient?.id) {
      return;
    }
    if (!profile) {
      notifications.show({ color: 'red', message: 'A provider profile is required to sign this order.' });
      return;
    }
    if (!signed) {
      notifications.show({ color: 'red', message: 'The ordering provider must sign this order before saving.' });
      return;
    }
    setSubmitting(true);
    try {
      let tina: Practitioner | undefined;
      if (automateTask) {
        const tinaResults = await medplum.searchResources('Practitioner', { name: 'Tina', _count: '20' });
        tina = tinaResults.find((practitioner) =>
          practitioner.name?.some((name: HumanName) =>
            `${name.given?.join(' ')} ${name.family ?? ''}`.toLowerCase().includes('tina')
          )
        );
        if (!tina) {
          throw new Error('Tina could not be found. RPM order was not created.');
        }
      }

      // Built (not written) up front: the enrollment CarePlan is committed in the same transaction as the order so a
      // failed order can never leave the patient showing RPM "Active" (SLE-825).
      const enrollmentEntry = await buildRpmEnrollmentActivationEntry(medplum, patient);
      const signer = getOrderSignerForSave(prescription, profile, editing);
      const signedAt = new Date().toISOString();
      const order: MedicationRequest = {
        resourceType: 'MedicationRequest',
        status: 'active',
        intent: 'order',
        authoredOn: new Date(`${orderDate}T12:00:00`).toISOString(),
        subject: createReference(patient),
        ...(editing && prescription ? { basedOn: [createReference(prescription)] } : {}),
        ...(encounterId ? { encounter: { reference: `Encounter/${encounterId}` } } : {}),
        medicationCodeableConcept: {
          coding: [{ system: RPM_ORDER_SYSTEM, code: 'rpm' }],
          text: 'RPM Order Form',
        },
        requester: signer,
        note: [
          {
            text: [
              'RPM has been ordered for this patient. Please initiate data review process on AirView.',
              `Diagnosis: ${diagnosis === 'Other' ? otherDiagnosis : diagnosis}`,
              `Secondary Diagnosis: ${secondaryDiagnosis || 'None'}`,
              `AHI from study results: ${ahi === undefined ? 'Not available' : ahi}`,
              `Date of Sleep Study: ${studyDate || 'Not documented'}`,
              `OSA Severity: ${severity || 'Not documented'}`,
              `Monitoring Duration: ${duration}`,
              `Monitoring Parameters: ${RPM_MONITORING_PARAMETERS.filter(([key]) => parameters[key])
                .map(([, label]) => label)
                .join(', ')}`,
              `Alert Thresholds: ${RPM_ALERT_THRESHOLDS.filter(([key]) => alerts[key])
                .map(([, label]) => label)
                .join(', ')}`,
              `PAP Device: ${[deviceManufacturer, deviceModel, deviceMode, devicePressure].filter(Boolean).join(' · ') || 'Not documented'}`,
              `Device Manufacturer: ${deviceManufacturer || 'Not documented'}`,
              `Device Model: ${deviceModel || 'Not documented'}`,
              `Device Mode: ${deviceMode || 'Not documented'}`,
              `Prescribed Pressure: ${devicePressure || 'Not documented'}`,
            ]
              .filter(Boolean)
              .join('\n'),
          },
        ],
      };
      // Signed orders are immutable. Editing creates an amended order linked through basedOn.
      // The order, its signature Provenance, the "Initiate RPM" Task, and the RPM enrollment CarePlan are written in one
      // transaction so neither an order nor an active enrollment can exist without the other (SLE-825).
      const orderKey = crypto.randomUUID();
      const orderFullUrl = `urn:uuid:${orderKey}`;
      const entry: BundleEntry[] = [
        {
          fullUrl: orderFullUrl,
          resource: withTenantScope(order),
          request: { method: 'POST', url: 'MedicationRequest' },
        },
        {
          resource: withTenantScope(buildRpmOrderSignatureProvenance(orderFullUrl, signer, signedAt)),
          request: { method: 'POST', url: 'Provenance' },
        },
      ];
      if (automateTask) {
        const task: Task = {
          resourceType: 'Task',
          identifier: [{ system: RPM_TASK_IDENTIFIER_SYSTEM, value: `${patient.id}|${orderKey}` }],
          status: 'requested',
          intent: 'order',
          priority: 'routine',
          authoredOn: new Date().toISOString(),
          requester: createReference(profile),
          owner: createReference(tina as Practitioner),
          for: createReference(patient),
          focus: { reference: orderFullUrl },
          code: { text: `Initiate RPM for ${patientName(patient)}` },
          description: 'RPM has been ordered for this patient. Please initiate data review process on AirView.',
          restriction: { period: { end: new Date(new Date().setHours(23, 59, 59, 999)).toISOString() } },
          extension: [{ url: TASK_ASSOCIATED_ACTION_URL, valueCode: 'airview' }],
        };
        entry.push({ resource: withTenantScope(task), request: { method: 'POST', url: 'Task' } });
      }
      entry.push(enrollmentEntry);
      const response = await medplum.executeBatch({ resourceType: 'Bundle', type: 'transaction', entry });
      // Unlike createResource/updateResource, executeBatch does not invalidate cached searches, so listeners of the
      // enrollment event (e.g. PatientStatusBar) would otherwise re-read the stale pre-transaction CarePlan search.
      for (const resourceType of ['CarePlan', 'MedicationRequest', 'Provenance', 'Task'] as const) {
        medplum.invalidateSearches(resourceType);
      }
      notifyRpmEnrollmentUpdated(patient.id);
      const createdOrder = response.entry?.find(
        (responseEntry: BundleEntry) => responseEntry.resource?.resourceType === 'MedicationRequest'
      )?.resource as MedicationRequest | undefined;
      if (!createdOrder?.id) {
        throw new Error('RPM order was created without a MedicationRequest ID.');
      }
      const createdTask = response.entry?.find(
        (responseEntry: BundleEntry) => responseEntry.resource?.resourceType === 'Task'
      )?.resource as Task | undefined;
      if (automateTask && !createdTask?.id) {
        throw new Error('RPM order was created without an Initiate RPM Task.');
      }

      try {
        await upsertRpmObservation(medplum, {
          patientId: patient.id,
          month: getCurrentRpmMonth(),
          code: RPM_OBSERVATION_CODES.monitoringStatus,
          value: { status: 'active' },
        });
      } catch (error) {
        Sentry.captureException(error);
        notifications.show({
          color: 'yellow',
          message: `RPM order and task created, but RPM status could not be set to Active: ${normalizeErrorString(error)}`,
        });
      }

      if (createdTask) {
        try {
          // An amendment keeps the original signer, so the PDF must name them rather than whoever saved the edit.
          const signingPractitioner =
            signer.reference === `Practitioner/${profile.id}`
              ? profile
              : await medplum.readReference<Practitioner>(signer);
          const document = await createPrescriptionDocument(
            medplum,
            patient,
            signingPractitioner,
            createdOrder,
            signedAt
          );
          await medplum.updateResource(withTenantScope(attachPrescriptionDocumentToTask(createdTask, document)));
          console.info(`RPM prescription PDF created for MedicationRequest/${createdOrder.id}`);
        } catch (error) {
          Sentry.captureException(error);
          console.error('Failed to create RPM prescription PDF', error);
        }
      }

      notifications.show({ color: 'green', icon: <IconCheck size={16} />, message: 'RPM order created' });
      navigate(-1);
    } catch (error) {
      Sentry.captureException(error);
      notifications.show({ color: 'red', message: normalizeErrorString(error) });
    } finally {
      setSubmitting(false);
    }
  };

  if (!patient || (readOnly && !prescription)) {
    return <LoadingOverlay visible />;
  }

  return (
    <Modal
      opened
      centered
      size="90%"
      title="RPM Order Form"
      onClose={() => navigate(-1)}
      styles={{
        content: { maxHeight: 'calc(100vh - 48px)' },
        body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', padding: 0 },
      }}
    >
      <PageContainer>
        <main className={classes.page}>
          <div className={classes.contextBanner}>
            <div className={classes.contextIcon}>
              <IconFilePlus size={18} />
            </div>
            <div className={classes.contextMain}>
              <Text className={classes.contextTitle}>Created from Encounter Note</Text>
              <Text className={classes.contextSubtitle}>
                <b>{patientName(patient)}</b> · Remote Patient Monitoring enrollment order
              </Text>
            </div>
            <Button variant="subtle" className={classes.contextLink} onClick={() => navigate(-1)}>
              Return to chart
            </Button>
          </div>
          <div className={classes.printLetterhead}>
            <div className={classes.printLetterheadTop}>
              <Text className={classes.printBrand}>POLARIS SLEEP</Text>
              <Text className={classes.printDocument}>Remote Patient Monitoring Enrollment Order</Text>
            </div>
            <div className={classes.printMeta}>
              <span>
                <b>Patient:</b> {patientName(patient)}
              </span>
              <span>
                <b>DOB:</b> {patient.birthDate ?? 'Not available'}
              </span>
              <span>
                <b>MRN:</b> {patient.identifier?.[0]?.value ?? 'Not available'}
              </span>
              <span>
                <b>Order date:</b> {readOnly && prescription?.authoredOn ? prescription.authoredOn.slice(0, 10) : today}
              </span>
              <span>
                <b>Provider:</b>{' '}
                {readOnly ? (prescription?.requester?.display ?? 'Signer not documented') : providerName(profile)}
              </span>
              <span>
                <b>NPI:</b> {providerNpi || 'Not available'}
              </span>
            </div>
          </div>
          <div className={classes.headerRow}>
            <div>
              <Text className={classes.breadcrumb}>Patients / {patientName(patient)} / Prescriptions</Text>
              <Title order={1}>Remote Patient Monitoring Order</Title>
              <Text className={classes.subtitle}>
                Obstructive Sleep Apnea — PAP Therapy Monitoring · Created from{' '}
                <span className={classes.encounterLink}>New Patient Intake</span> · <b>{patientName(patient)}</b> ·{' '}
                {formatDisplayDate(orderDate)}
              </Text>
            </div>
            <Group className={classes.headerActions}>
              <Button
                variant="default"
                leftSection={<IconDownload size={16} />}
                onClick={() => handleExport().catch(() => undefined)}
                disabled={!canExport}
                loading={exporting}
                title={canExport ? undefined : 'Available once the order has been signed and saved'}
              >
                Export PDF
              </Button>
              {readOnly && storedDocument?.content?.[0]?.attachment?.url && (
                <Button variant="default" onClick={() => handleViewStoredDocument().catch(console.error)}>
                  View Signed PDF
                </Button>
              )}
              {readOnly && (
                <Button variant="default" leftSection={<IconEdit size={16} />} onClick={() => setEditing(true)}>
                  Edit
                </Button>
              )}
              <Button variant="default" onClick={() => navigate(-1)}>
                {readOnly ? 'Back' : 'Cancel'}
              </Button>
            </Group>
          </div>

          <div className={classes.cards}>
            <Card className={classes.card} withBorder={false}>
              <div className={classes.cardHeader}>
                <Title order={2}>Patient Information</Title>
                {!readOnly && (
                  <Button
                    size="compact-sm"
                    className={classes.editButton}
                    variant="filled"
                    leftSection={<IconEdit size={14} />}
                    onClick={() => openInNewWindow(`/Patient/${patient.id}/profile`)}
                  >
                    Edit in Patient Chart
                  </Button>
                )}
              </div>
              <div className={classes.grid}>
                <TextInput
                  label="Patient Name"
                  value={patientName(patient)}
                  readOnly
                  className={classes.readOnlyField}
                  withAsterisk
                />
                <TextInput
                  label="Date of Birth"
                  value={patient.birthDate ?? 'Not available'}
                  type="date"
                  readOnly
                  className={classes.readOnlyField}
                  withAsterisk
                />
              </div>
            </Card>

            <Card className={classes.card} withBorder={false}>
              <div className={classes.cardHeader}>
                <Title order={2}>Ordering Provider</Title>
                {!readOnly && (
                  <Button
                    size="compact-sm"
                    className={classes.editButton}
                    variant="filled"
                    leftSection={<IconEdit size={14} />}
                    onClick={() => openInNewWindow('/settings')}
                  >
                    Edit in Account Settings
                  </Button>
                )}
              </div>
              <div className={classes.grid}>
                <TextInput
                  label="Provider Name"
                  value={
                    readOnly ? (prescription?.requester?.display ?? 'Signer not documented') : providerName(profile)
                  }
                  readOnly
                  className={classes.readOnlyField}
                  withAsterisk
                />
                <TextInput
                  label="NPI"
                  value={providerNpi || 'Not available'}
                  readOnly
                  className={classes.readOnlyField}
                  withAsterisk
                />
                <TextInput
                  label="Date of Order"
                  type="date"
                  value={orderDate}
                  onChange={(event) => setOrderDate(event.currentTarget.value)}
                  readOnly={readOnly}
                  withAsterisk
                />
              </div>
            </Card>

            <Card className={classes.card} withBorder={false}>
              <div className={classes.cardHeader}>
                <Title order={2}>Diagnosis</Title>
                <Text className={classes.autofill}>From encounter</Text>
              </div>
              <div className={classes.grid}>
                <Select
                  label="Suspected Diagnosis"
                  data={DIAGNOSIS_OPTIONS}
                  value={diagnosis}
                  onChange={(value) => setDiagnosis(value ?? DIAGNOSIS_OPTIONS[0].value)}
                  disabled={readOnly}
                  withAsterisk
                />
                <Select
                  label="Secondary Suspected Diagnosis"
                  data={[{ value: '', label: 'None' }, ...DIAGNOSIS_OPTIONS]}
                  value={secondaryDiagnosis}
                  onChange={(value) => setSecondaryDiagnosis(value ?? '')}
                  disabled={readOnly}
                />
              </div>
              <Text className={classes.subLabel}>
                OSA Severity <span>(based on diagnostic study)</span>
              </Text>
              <Group className={classes.choiceRow} gap="sm">
                {SEVERITY_OPTIONS.map((option) => (
                  <Radio
                    key={option.value}
                    label={
                      <>
                        <span>{option.label}</span> <span className={classes.choiceSub}>{option.sub}</span>
                      </>
                    }
                    checked={severity === option.value}
                    onChange={() => setSeverity(option.value)}
                    disabled={readOnly}
                    color="#640040"
                    className={classes.choice}
                  />
                ))}
              </Group>
              <div className={`${classes.grid} ${classes.studyGrid}`}>
                <TextInput
                  label="Diagnostic AHI / REI"
                  placeholder="Pending study"
                  value={ahi === undefined ? '' : `${ahi}`}
                  readOnly={readOnly}
                  description="events/hour"
                />
                <TextInput
                  label="Date of Sleep Study"
                  type="date"
                  value={studyDate}
                  onChange={(event) => setStudyDate(event.currentTarget.value)}
                  readOnly={readOnly}
                />
              </div>
            </Card>

            <Card className={`${classes.card} ${classes.papCard}`} withBorder={false}>
              <Title order={2}>PAP Device Information</Title>
              <div className={classes.grid}>
                <TextInput
                  label="Device Manufacturer"
                  value={deviceManufacturer}
                  onChange={(event) => setDeviceManufacturer(event.currentTarget.value)}
                  readOnly={readOnly}
                />
                <TextInput
                  label="Device Model"
                  value={deviceModel}
                  onChange={(event) => setDeviceModel(event.currentTarget.value)}
                  readOnly={readOnly}
                />
                <Select
                  label="Mode"
                  data={['CPAP', 'APAP', 'BiPAP']}
                  value={deviceMode}
                  onChange={(value) => setDeviceMode(value ?? 'APAP')}
                  disabled={readOnly}
                />
                <TextInput
                  label="Prescribed Pressure(s)"
                  placeholder="e.g. 5–15 cmH₂O"
                  value={devicePressure}
                  onChange={(event) => setDevicePressure(event.currentTarget.value)}
                  readOnly={readOnly}
                />
              </div>
            </Card>

            <Card className={`${classes.card} ${classes.alertCard}`} withBorder={false}>
              <Title order={2}>Alert Thresholds</Title>
              <Text className={classes.sectionIntro}>Notify the clinical team when any selected condition is met.</Text>
              <Stack gap="sm">
                {RPM_ALERT_THRESHOLDS.map(([key, label]) => (
                  <Checkbox
                    key={key}
                    label={label}
                    checked={alerts[key]}
                    onChange={() => setAlerts((current) => ({ ...current, [key]: !current[key] }))}
                    readOnly={readOnly}
                    color="#640040"
                    className={classes.choice}
                  />
                ))}
              </Stack>
            </Card>

            <Card className={`${classes.card} ${classes.signatureCard}`} withBorder={false}>
              <Title order={2}>Provider Signature</Title>
              <div className={classes.signatureRow}>
                <Button
                  variant="default"
                  className={signed ? classes.signed : classes.signaturePad}
                  onClick={() => setSigned(true)}
                  disabled={readOnly && signed}
                >
                  {signatureLabel}
                </Button>
                <TextInput label="Date of Order" value={orderDate} readOnly />
              </div>
            </Card>

            <Card className={`${classes.card} ${classes.rpmCard}`} withBorder={false}>
              <Title order={2}>RPM Enrollment Order</Title>
              <label className={classes.enroll}>
                <Checkbox checked readOnly color="#640040" aria-label="RPM enrollment active" />
                <span>
                  <b>Enroll patient in Remote Patient Monitoring</b>
                  <small>For PAP therapy adherence and efficacy monitoring.</small>
                </span>
              </label>
              <Stack gap="sm" mt="lg">
                <Text className={classes.subLabel}>Monitoring Duration</Text>
                <Group className={classes.choiceRow} gap="sm">
                  {DURATION_OPTIONS.map((option) => (
                    <Radio
                      key={option}
                      label={option}
                      checked={duration === option}
                      onChange={() => setDuration(option)}
                      disabled={readOnly}
                      color="#640040"
                      className={classes.choice}
                    />
                  ))}
                </Group>
                <Text className={classes.subLabel}>Monitoring Parameters</Text>
                <Text className={classes.sectionIntro}>Transmitted via PAP device modem / cloud platform.</Text>
                <Group className={classes.choiceRow} gap="sm">
                  {RPM_MONITORING_PARAMETERS.map(([key, label]) => (
                    <Checkbox
                      key={key}
                      label={label}
                      checked={parameters[key]}
                      onChange={() => setParameters((current) => ({ ...current, [key]: !current[key] }))}
                      readOnly={readOnly}
                      color="#640040"
                      className={classes.choice}
                    />
                  ))}
                </Group>
              </Stack>
            </Card>
          </div>

          <Modal
            opened={Boolean(storedDocumentUrl)}
            onClose={() => setStoredDocumentUrl(undefined)}
            title="Signed Prescription"
            size="xl"
          >
            {storedDocumentUrl && (
              <iframe
                title="Signed Prescription"
                src={storedDocumentUrl}
                style={{ width: '100%', height: '70vh', border: 0 }}
              />
            )}
          </Modal>

          <footer className={classes.footer}>
            <Text className={classes.footerNote}>
              <IconLock size={15} aria-hidden="true" />
              {readOnly
                ? 'This RPM order is read-only.'
                : 'RPM status will be set to Active when this order is submitted.'}
            </Text>
            {readOnly ? (
              <Button
                className={classes.submit}
                leftSection={<IconDownload size={16} />}
                onClick={() => handleExport().catch(() => undefined)}
                disabled={!canExport}
                loading={exporting}
              >
                Export PDF
              </Button>
            ) : (
              <Group gap="sm">
                <Button variant="default" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button
                  className={classes.submit}
                  leftSection={<IconFilePlus size={16} />}
                  loading={submitting}
                  onClick={() => handleSubmit().catch(() => undefined)}
                >
                  Save
                </Button>
              </Group>
            )}
          </footer>
        </main>
      </PageContainer>
    </Modal>
  );
}
